import { useLocation, useNavigate } from 'react-router-dom';

import { useMyPermissions } from '@/api/getMyPermissions';
import { setGameScope, setSandboxScope } from '@/lib/api-client';
import { storeGameSandbox } from '@/lib/utils';
import { pagePermissionsFor } from '@/models/pagePermissions';
import { holdsPermissions } from '@/models/permission';

/**
 * If the user is on an entity-specific page (e.g., player detail, snapshot detail),
 * navigate to the parent list to avoid 404 errors.
 */
export const getSafeNavigationPath = (
  currentPath: string,
  gameCode: string,
  newSandbox: string,
): { path: string; preserveSearch: boolean } => {
  const basePath = `/game/${gameCode}/${newSandbox}`;

  const playerDetailPattern = /\/players\/\d+/;
  if (playerDetailPattern.test(currentPath)) {
    return { path: `${basePath}/players`, preserveSearch: false };
  }

  const snapshotDetailPattern = /\/store\/snapshot\/(?!new)[^/]+/;
  if (snapshotDetailPattern.test(currentPath)) {
    return { path: `${basePath}/store/snapshot/new`, preserveSearch: false };
  }

  const pathAfterSandbox = currentPath.replace(
    /^\/game\/[^/]+\/[^/]+/,
    basePath,
  );
  return { path: pathAfterSandbox, preserveSearch: true };
};

interface UseGameSwitchOptions {
  onBeforeSwitch?: () => void;
}

/**
 * Navigates to the sandboxes page for the new game.
 */
export const useGameSwitch = (options: UseGameSwitchOptions = {}) => {
  const navigate = useNavigate();

  const switchGame = (currentGameCode: string, newGameCode: string) => {
    if (newGameCode === currentGameCode) return;

    options.onBeforeSwitch?.();

    navigate(`/game/${newGameCode}/sandboxes`);
  };

  return { switchGame };
};

interface UseSandboxSwitchOptions {
  onBeforeSwitch?: () => void;
}

/**
 * Handles API defaults, localStorage persistence, and safe navigation.
 * Preserves URL query parameters when staying on the same type of page.
 */
export const useSandboxSwitch = (options: UseSandboxSwitchOptions = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data } = useMyPermissions();

  const switchSandbox = (
    gameCode: string,
    currentSandbox: string,
    newSandbox: string,
  ) => {
    if (newSandbox === currentSandbox) return;

    options.onBeforeSwitch?.();

    // Before invalidating queries, so refetches use the new context.
    setGameScope(gameCode);
    setSandboxScope(newSandbox);

    storeGameSandbox(gameCode, newSandbox);

    const { path, preserveSearch } = getSafeNavigationPath(
      location.pathname,
      gameCode,
      newSandbox,
    );
    const fullPath = preserveSearch ? `${path}${location.search}` : path;

    // Staying on the same page only works when it is open in the new Sandbox: Grants
    // are per-Sandbox. When it is not, go to the Sandbox root, whose index picks the
    // first page this viewer can open there — a 403 for a Sandbox the app chose to
    // enter would be the app's fault, not the user's.
    const base = `/game/${gameCode}/${newSandbox}`;
    const codes = pagePermissionsFor(path.replace(`${base}/`, ''));
    const opens =
      !codes ||
      // Grants not read yet is not the same as no Grants: keep the path and let the
      // destination's own route gate answer, rather than silently dropping the user
      // on the Sandbox root.
      !data ||
      holdsPermissions(
        data.roles,
        { oneOf: codes },
        {
          gameCode,
          sandboxCode: newSandbox,
        },
      );

    navigate(opens ? fullPath : base);
  };

  return { switchSandbox };
};
